import { useCallback, useEffect, useState } from 'react'

import { ACCEPTED_AUDIO_CONTENT_TYPES } from '@common/constants/contentTypes'
import { FormProvider, useForm } from 'react-hook-form'

import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { FormField } from '@/components/ui/form/form-field'
import { ChooseFile } from '@/modules/chatting/ui/components/ChooseFile'

import { DialogProps } from '../types'
import { ChosenAudio } from './ChosenAudio'
import { useUploadAudio } from './useUploadAudio'
import { excludeExtension, getFileDuration } from './utils'
import {
  UPLOAD_AUDIO_FORM_ID,
  UploadAudioFormValues,
  uploadAudioFormResolver,
} from './validation'

interface Props extends DialogProps {
  audio: File
  onUpload?: () => void
}

export const UploadAudioDialog = ({
  isOpen,
  audio,
  onClose,
  onUpload,
}: Props) => {
  const form = useForm<UploadAudioFormValues>({
    resolver: uploadAudioFormResolver,
    defaultValues: {
      name: excludeExtension(audio.name),
      description: '',
      file: audio,
    },
  })

  const handleUpload = useCallback(() => {
    onUpload?.()
    onClose()
  }, [onUpload, onClose])

  const { uploadAudio, uploadProgress, setUploadProgress } = useUploadAudio({
    onUpload: handleUpload,
  })
  const [duration, setDuration] = useState(0)

  const handleChooseFile = useCallback(
    (file: File) => {
      form.setValue('file', file, { shouldDirty: true, shouldValidate: true })
      form.setValue('name', excludeExtension(file.name), {
        shouldDirty: true,
        shouldValidate: true,
      })
      getFileDuration(file).then(setDuration)
    },
    [form],
  )

  const resetState = () => {
    form.reset({
      name: '',
      description: '',
      file: undefined,
    })
    setDuration(0)
    setUploadProgress(0)
  }

  const file = form.watch('file')

  useEffect(() => {
    if (file) {
      getFileDuration(file).then(setDuration)
    }
  }, [file])

  return (
    <Dialog
      panelClassName="lg:max-w-[533px]"
      show={isOpen}
      onClose={onClose}
      bigDialog
    >
      <Dialog.Header>
        <Dialog.Title>Upload Audio</Dialog.Title>
      </Dialog.Header>
      <Dialog.Description className="mt-6 block">
        <FormProvider {...form}>
          <form
            id={UPLOAD_AUDIO_FORM_ID}
            className="space-y-6"
            onSubmit={form.handleSubmit(uploadAudio)}
          >
            {file ? (
              <ChosenAudio
                fileUrl={URL.createObjectURL(file)}
                name={file.name}
                duration={duration}
                onDelete={resetState}
                uploadProgress={uploadProgress}
                isUploading={form.formState.isSubmitting}
              />
            ) : (
              <ChooseFile
                setFile={handleChooseFile}
                acceptedContentTypes={ACCEPTED_AUDIO_CONTENT_TYPES}
              />
            )}
            <FormField
              name="name"
              label="Title"
              placeholder="Example Audio"
            />
            <FormField
              name="description"
              label="Description"
              placeholder="Optional"
            />
          </form>
        </FormProvider>
      </Dialog.Description>
      <Dialog.ClosableFooter onClose={onClose}>
        <Button
          type="submit"
          form={UPLOAD_AUDIO_FORM_ID}
          isLoading={form.formState.isSubmitting}
          disabled={!form.formState.isValid}
        >
          Upload
        </Button>
      </Dialog.ClosableFooter>
    </Dialog>
  )
}
